import { Injectable } from '@angular/core';
import { getAngularJSService } from '../ng-upgrade.utils';
import {
  AdvertStatusCountResponse,
  CarInvestData,
  CatalogItem,
  CheckRegisteredVehicleResponse,
  CredentialsCheckResponse,
  Entity,
  EntityIdResponse,
  GepetoCommentsResponse,
  ImageSchemaResponse,
  InspectionReport,
  LoggedUser,
  Pagination,
  PlateVinSearchResponse,
  Portal,
  PortalAvailableEntity,
  PortalsResponse,
  ReferenceValueResponse,
  Reservation,
  Vehicle,
  VehicleHistoricFilter,
  VehicleHistoricListResult,
  VehicleListParams,
  VehicleListResponse,
  VehicleRelationship,
  VehiclesToRemoveResponse,
} from '@core/models';

interface LegacyHttpResponse<T> {
  data: T;
}

interface LegacyVehicleFactory {
  plateSearch(data: { plate: string }): Promise<LegacyHttpResponse<PlateVinSearchResponse>>;
  vinSearch(data: { vin: string }): Promise<LegacyHttpResponse<PlateVinSearchResponse>>;
  getColors(): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getDirectionsType(): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getFuelTypes(): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getTransmissionTypes(): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getEntities(): Promise<LegacyHttpResponse<Entity[]>>;
  getEntitiesPortalList(): Promise<LegacyHttpResponse<unknown>>;
  getAddress(entityAAId: number): Promise<LegacyHttpResponse<unknown>>;
  getOptionals(): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getVehicleTypes(): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getVehicleSection(kindId?: number): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getImageSchema(): Promise<LegacyHttpResponse<ImageSchemaResponse>>;
  getVehicleBrands(year?: number, kindId?: number): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getPortals(): Promise<LegacyHttpResponse<PortalsResponse>>;
  getVehicleModels(brand: number, year?: number): Promise<LegacyHttpResponse<CatalogItem[]>>;
  getVehicleVersions(model: number, year?: number): Promise<LegacyHttpResponse<CatalogItem[]>>;
  checkAlreadyRegisteredVehicle(
    plateOrVin: string,
    entities: number[]
  ): Promise<LegacyHttpResponse<CheckRegisteredVehicleResponse>>;
  uploadVehiclePhoto(formData: FormData): Promise<LegacyHttpResponse<unknown>>;
  createInspectionReport(vehicleId: number, file: File): Promise<LegacyHttpResponse<unknown>>;
  getInspectionReport(vehicleId: number): Promise<LegacyHttpResponse<InspectionReport>>;
  deleteInspectionReport(vehicleId: number): Promise<LegacyHttpResponse<void>>;
  getReferenceValue(
    plate: string,
    versionId: number,
    optionals: number[]
  ): Promise<LegacyHttpResponse<ReferenceValueResponse>>;
  createVehicle(data: Partial<Vehicle>): Promise<LegacyHttpResponse<Vehicle>>;
  updateVehicle(data: Partial<Vehicle>, id: number): Promise<LegacyHttpResponse<Vehicle>>;
  updateFlagOfStock(data: Partial<Vehicle>, id: number): Promise<LegacyHttpResponse<void>>;
  updateFlagFeatured(data: Partial<Vehicle>, id: number): Promise<LegacyHttpResponse<void>>;
  updateFlagLandpage(data: Partial<Vehicle>, id: number): Promise<LegacyHttpResponse<void>>;
  updateVehicleFlag(
    vehicleId: number,
    flagId: number,
    value: boolean
  ): Promise<LegacyHttpResponse<void>>;
  getVehicleById(id: number): Promise<LegacyHttpResponse<Vehicle>>;
  getVehicleByIdWithAdverts(id: number): Promise<LegacyHttpResponse<Vehicle>>;
  setVehicleAsReserved(vehicle: Vehicle): Promise<LegacyHttpResponse<void>>;
  setVehicleAsAvailable(vehicle: Vehicle): Promise<LegacyHttpResponse<void>>;
  getVehicleListWithParams(
    params: VehicleListParams
  ): Promise<LegacyHttpResponse<VehicleListResponse>>;
  getAdvertStatusCount(
    params: VehicleListParams
  ): Promise<LegacyHttpResponse<AdvertStatusCountResponse>>;
  getVehicleHistoricList(
    pagination: Pagination,
    filter: VehicleHistoricFilter
  ): Promise<LegacyHttpResponse<VehicleHistoricListResult>>;
  deleteVehicleById(id: number, username: string): Promise<LegacyHttpResponse<void>>;
  syncVehicle(data: Partial<Vehicle>, username: string): Promise<LegacyHttpResponse<unknown>>;
  clearNotifications(id: number, type: string): Promise<LegacyHttpResponse<void>>;
  getVehicleQuestionsById(
    vehicle: Vehicle,
    loggedUser: LoggedUser
  ): Promise<LegacyHttpResponse<unknown>>;
  addResponse(
    data: { id: number; answerValue: string },
    vehicle: Vehicle,
    loggedUser: LoggedUser
  ): Promise<LegacyHttpResponse<unknown>>;
  getVehicleReservationById(
    vehicle: Vehicle,
    loggedUser: LoggedUser
  ): Promise<LegacyHttpResponse<Reservation[]>>;
  acceptVehicleReservationById(
    vehicle: Vehicle,
    loggedUser: LoggedUser,
    reservationId: number
  ): Promise<LegacyHttpResponse<unknown>>;
  cancelVehicleReservationById(
    vehicle: Vehicle,
    loggedUser: LoggedUser,
    reservationId: number
  ): Promise<LegacyHttpResponse<unknown>>;
  getAutoflashListByPlate(
    plate: string,
    autoflashAAId: number
  ): Promise<LegacyHttpResponse<string[]>>;
  getPortalsMatchs(
    brandId: number,
    modelId: number,
    versionId: number,
    kindId: number
  ): Promise<LegacyHttpResponse<unknown>>;
  getVehiclePortalsValue(vehicleId: number): Promise<LegacyHttpResponse<unknown>>;
  restoreDataVehicle(
    vehicleId: number,
    plate: string,
    vin: string,
    hgId: number
  ): Promise<LegacyHttpResponse<unknown>>;
  credentialsCheck(entityId: number): Promise<LegacyHttpResponse<CredentialsCheckResponse>>;
  generateCommentsByGepeto(data: unknown): Promise<LegacyHttpResponse<GepetoCommentsResponse>>;
  getPortalsAvailable(entitiesAAId: number[]): Promise<LegacyHttpResponse<PortalAvailableEntity[]>>;
  getVehiclesToRemove(
    portalAAId: number,
    entitiesAAIds: number[],
    paramsPagination?: { limit?: number; page?: number }
  ): Promise<LegacyHttpResponse<VehiclesToRemoveResponse>>;
  saveLog(vin: string, plate: string): Promise<LegacyHttpResponse<unknown>>;
  getEntityId(entityAAId: number): Promise<LegacyHttpResponse<EntityIdResponse>>;
  getData(
    versionId: number,
    entityId: number,
    stateUF: string
  ): Promise<LegacyHttpResponse<CarInvestData>>;
  replicateVehicle(
    vehicleId: number,
    entitiesSelected: number[]
  ): Promise<LegacyHttpResponse<unknown>>;
  getTranslate(): Promise<unknown>;
  getVehicleRelationship(vehicleId: number): Promise<VehicleRelationship>;
}

@Injectable({ providedIn: 'root' })
export class VehicleService {
  private vehicleFactory: LegacyVehicleFactory | null = null;

  private get factory(): LegacyVehicleFactory {
    if (this.vehicleFactory) {
      return this.vehicleFactory;
    }
    const svc = getAngularJSService<LegacyVehicleFactory>('vehicleFactory');
    if (!svc) {
      throw new Error(
        'vehicleFactory (AngularJS) is unavailable. AngularJS may not have been bootstrapped yet.'
      );
    }
    this.vehicleFactory = svc;
    return this.vehicleFactory;
  }

  async plateSearch(data: { plate: string }): Promise<PlateVinSearchResponse> {
    const response = await this.factory.plateSearch(data);
    return response.data;
  }

  async vinSearch(data: { vin: string }): Promise<PlateVinSearchResponse> {
    const response = await this.factory.vinSearch(data);
    return response.data;
  }

  async getColors(): Promise<CatalogItem[]> {
    const response = await this.factory.getColors();
    return response.data;
  }

  async getDirectionsType(): Promise<CatalogItem[]> {
    const response = await this.factory.getDirectionsType();
    return response.data;
  }

  async getFuelTypes(): Promise<CatalogItem[]> {
    const response = await this.factory.getFuelTypes();
    return response.data;
  }

  async getTransmissionTypes(): Promise<CatalogItem[]> {
    const response = await this.factory.getTransmissionTypes();
    return response.data;
  }

  async getEntities(): Promise<Entity[]> {
    const response = await this.factory.getEntities();
    return response.data;
  }

  async getEntitiesPortalList(): Promise<unknown> {
    const response = await this.factory.getEntitiesPortalList();
    return response.data;
  }

  async getAddress(entityAAId: number): Promise<unknown> {
    const response = await this.factory.getAddress(entityAAId);
    return response.data;
  }

  async getOptionals(): Promise<CatalogItem[]> {
    const response = await this.factory.getOptionals();
    return response.data;
  }

  async getVehicleTypes(): Promise<CatalogItem[]> {
    const response = await this.factory.getVehicleTypes();
    return response.data;
  }

  async getVehicleSection(kindId?: number): Promise<CatalogItem[]> {
    const response = await this.factory.getVehicleSection(kindId);
    return response.data;
  }

  async getImageSchema(): Promise<ImageSchemaResponse> {
    const response = await this.factory.getImageSchema();
    return response.data;
  }

  async getVehicleBrands(year?: number, kindId?: number): Promise<CatalogItem[]> {
    const response = await this.factory.getVehicleBrands(year, kindId);
    return response.data;
  }

  async getPortals(): Promise<Portal[]> {
    // `/portal` responde `{ portals: Portal[], credentials: [...] }` — não um array puro
    // (paridade com panel.vehicle-list.controller.js:203, que já lê `response.data.portals`).
    const response = await this.factory.getPortals();
    return response.data.portals;
  }

  async getVehicleModels(brand: number, year?: number): Promise<CatalogItem[]> {
    const response = await this.factory.getVehicleModels(brand, year);
    return response.data;
  }

  async getVehicleVersions(model: number, year?: number): Promise<CatalogItem[]> {
    const response = await this.factory.getVehicleVersions(model, year);
    return response.data;
  }

  async checkAlreadyRegisteredVehicle(
    plateOrVin: string,
    entities: number[]
  ): Promise<CheckRegisteredVehicleResponse> {
    const response = await this.factory.checkAlreadyRegisteredVehicle(plateOrVin, entities);
    return response.data;
  }

  async uploadVehiclePhoto(formData: FormData): Promise<unknown> {
    const response = await this.factory.uploadVehiclePhoto(formData);
    return response.data;
  }

  async createInspectionReport(vehicleId: number, file: File): Promise<unknown> {
    const response = await this.factory.createInspectionReport(vehicleId, file);
    return response.data;
  }

  async getInspectionReport(vehicleId: number): Promise<InspectionReport> {
    const response = await this.factory.getInspectionReport(vehicleId);
    return response.data;
  }

  async deleteInspectionReport(vehicleId: number): Promise<void> {
    await this.factory.deleteInspectionReport(vehicleId);
  }

  async getReferenceValue(
    plate: string,
    versionId: number,
    optionals: number[]
  ): Promise<ReferenceValueResponse> {
    const response = await this.factory.getReferenceValue(plate, versionId, optionals);
    return response.data;
  }

  async createVehicle(data: Partial<Vehicle>): Promise<Vehicle> {
    const response = await this.factory.createVehicle(data);
    return response.data;
  }

  async updateVehicle(data: Partial<Vehicle>, id: number): Promise<Vehicle> {
    const response = await this.factory.updateVehicle(data, id);
    return response.data;
  }

  async updateFlagOfStock(data: Partial<Vehicle>, id: number): Promise<void> {
    await this.factory.updateFlagOfStock(data, id);
  }

  async updateFlagFeatured(data: Partial<Vehicle>, id: number): Promise<void> {
    await this.factory.updateFlagFeatured(data, id);
  }

  async updateFlagLandpage(data: Partial<Vehicle>, id: number): Promise<void> {
    await this.factory.updateFlagLandpage(data, id);
  }

  async updateVehicleFlag(vehicleId: number, flagId: number, value: boolean): Promise<void> {
    await this.factory.updateVehicleFlag(vehicleId, flagId, value);
  }

  async getVehicleById(id: number): Promise<Vehicle> {
    const response = await this.factory.getVehicleById(id);
    return response.data;
  }

  async getVehicleByIdWithAdverts(id: number): Promise<Vehicle> {
    const response = await this.factory.getVehicleByIdWithAdverts(id);
    return response.data;
  }

  async setVehicleAsReserved(vehicle: Vehicle): Promise<void> {
    await this.factory.setVehicleAsReserved(vehicle);
  }

  async setVehicleAsAvailable(vehicle: Vehicle): Promise<void> {
    await this.factory.setVehicleAsAvailable(vehicle);
  }

  async getVehicleListWithParams(params: VehicleListParams): Promise<VehicleListResponse> {
    const response = await this.factory.getVehicleListWithParams(params);
    return response.data;
  }

  async getAdvertStatusCount(params: VehicleListParams): Promise<AdvertStatusCountResponse> {
    const response = await this.factory.getAdvertStatusCount(params);
    return response.data;
  }

  async getVehicleHistoricList(
    pagination: Pagination,
    filter: VehicleHistoricFilter
  ): Promise<VehicleHistoricListResult> {
    const response = await this.factory.getVehicleHistoricList(pagination, filter);
    return response.data;
  }

  async deleteVehicleById(id: number, username: string): Promise<void> {
    await this.factory.deleteVehicleById(id, username);
  }

  async syncVehicle(data: Partial<Vehicle>, username: string): Promise<unknown> {
    const response = await this.factory.syncVehicle(data, username);
    return response.data;
  }

  async clearNotifications(id: number, type: string): Promise<void> {
    await this.factory.clearNotifications(id, type);
  }

  async getVehicleQuestionsById(vehicle: Vehicle, loggedUser: LoggedUser): Promise<unknown> {
    const response = await this.factory.getVehicleQuestionsById(vehicle, loggedUser);
    return response.data;
  }

  async addResponse(
    data: { id: number; answerValue: string },
    vehicle: Vehicle,
    loggedUser: LoggedUser
  ): Promise<unknown> {
    const response = await this.factory.addResponse(data, vehicle, loggedUser);
    return response.data;
  }

  async getVehicleReservationById(
    vehicle: Vehicle,
    loggedUser: LoggedUser
  ): Promise<Reservation[]> {
    const response = await this.factory.getVehicleReservationById(vehicle, loggedUser);
    return response.data;
  }

  async acceptVehicleReservationById(
    vehicle: Vehicle,
    loggedUser: LoggedUser,
    reservationId: number
  ): Promise<unknown> {
    const response = await this.factory.acceptVehicleReservationById(
      vehicle,
      loggedUser,
      reservationId
    );
    return response.data;
  }

  async cancelVehicleReservationById(
    vehicle: Vehicle,
    loggedUser: LoggedUser,
    reservationId: number
  ): Promise<unknown> {
    const response = await this.factory.cancelVehicleReservationById(
      vehicle,
      loggedUser,
      reservationId
    );
    return response.data;
  }

  async getAutoflashListByPlate(plate: string, autoflashAAId: number): Promise<string[]> {
    const response = await this.factory.getAutoflashListByPlate(plate, autoflashAAId);
    return response.data;
  }

  async getPortalsMatchs(
    brandId: number,
    modelId: number,
    versionId: number,
    kindId: number
  ): Promise<unknown> {
    const response = await this.factory.getPortalsMatchs(brandId, modelId, versionId, kindId);
    return response.data;
  }

  async getVehiclePortalsValue(vehicleId: number): Promise<unknown> {
    const response = await this.factory.getVehiclePortalsValue(vehicleId);
    return response.data;
  }

  async restoreDataVehicle(
    vehicleId: number,
    plate: string,
    vin: string,
    hgId: number
  ): Promise<unknown> {
    const response = await this.factory.restoreDataVehicle(vehicleId, plate, vin, hgId);
    return response.data;
  }

  async credentialsCheck(entityId: number): Promise<CredentialsCheckResponse> {
    const response = await this.factory.credentialsCheck(entityId);
    return response.data;
  }

  async generateCommentsByGepeto(data: unknown): Promise<GepetoCommentsResponse> {
    const response = await this.factory.generateCommentsByGepeto(data);
    return response.data;
  }

  async getPortalsAvailable(entitiesAAId: number[]): Promise<PortalAvailableEntity[]> {
    const response = await this.factory.getPortalsAvailable(entitiesAAId);
    return response.data;
  }

  async getVehiclesToRemove(
    portalAAId: number,
    entitiesAAIds: number[],
    paramsPagination?: { limit?: number; page?: number }
  ): Promise<unknown> {
    const response = await this.factory.getVehiclesToRemove(
      portalAAId,
      entitiesAAIds,
      paramsPagination
    );
    return response.data;
  }

  async saveLog(vin: string, plate: string): Promise<unknown> {
    const response = await this.factory.saveLog(vin, plate);
    return response.data;
  }

  async getEntityId(entityAAId: number): Promise<EntityIdResponse> {
    const response = await this.factory.getEntityId(entityAAId);
    return response.data;
  }

  async getData(versionId: number, entityId: number, stateUF: string): Promise<CarInvestData> {
    const response = await this.factory.getData(versionId, entityId, stateUF);
    return response.data;
  }

  async replicateVehicle(vehicleId: number, entitiesSelected: number[]): Promise<unknown> {
    const response = await this.factory.replicateVehicle(vehicleId, entitiesSelected);
    return response.data;
  }

  async getTranslate(): Promise<unknown> {
    return this.factory.getTranslate();
  }

  async getVehicleRelationship(vehicleId: number): Promise<VehicleRelationship> {
    return this.factory.getVehicleRelationship(vehicleId);
  }
}
